import { getInternalOverlay, getInternalOverlays, getReferenceOverlay } from "../api";
import translator from '../utils/translation';
import OVERLAY_STYLES from "../utils/styles";
import { Storage, STORAGE_KEYS } from "../utils/store";
/**
 * Extension du layer geojson pour 
 * afficher les services (externes) d'overlay qui renvoient du geojosn
 */
const lazyGeoJSON = L.GeoJSON.extend({
    initialize: function (remote, options) {
        const userOnEachFeature = options.onEachFeature;
        options.onEachFeature = (feature, layer) => {
            if (userOnEachFeature) {
                userOnEachFeature(feature, layer);
            }

            if (feature.geometry.type !== 'Point') {
                layer.on('click', (e) => {
                    layer.bindPopup(L.overlayPopup(feature.properties, { latlng: e.latlng, featureId: feature.id })).openPopup();
                });
            }
            layer.bindTooltip(feature.properties.name ?? '--');

            const style = OVERLAY_STYLES[options.type?.name ?? 'default'];

            if (typeof style === 'function') {
                layer.setStyle({
                    ...style(feature.properties),
                    ...(feature.properties.style || {})
                });
            }
        };

        L.GeoJSON.prototype.initialize.call(this, [], {
                ...options
        });
        
        this.on('add', () => this._fetchData(remote, options));
        this.on('remove', () => this.clearLayers());
    },

    _fetchData: function(remote, options) {
        if (typeof remote === "string") {
            let headers = options?.headers ?? {};

            if(!remote.startsWith('http')) {
                remote = `${process.env.GENDLOC_BACKEND_URL}${remote}`;
                headers = {
                    'Authorization': `Bearer ${Storage.get(STORAGE_KEYS.JWT)}`
                };
            }

            this._url = remote;
            this._headers = headers;

            remote = fetch(remote, { headers: headers })
                .then(rep => rep.json());
        }

        remote.then(geoJson => {
            this.clearLayers();
            this.addData(geoJson);
        })
        .catch(err => console.error(err));
    },

    _refreshLayer: function() {
        if (!this._url || !this._headers) {
            console.error('lazyGeoJson layer: No url or headers set for refreshing data');
            return;
        }

        fetch(this._url, { headers: this._headers })
            .then(rep => rep.json())
            .then(geoJSON => {
                this.clearLayers();
                this.addData(geoJSON);
            })
            .catch(err => console.error('lazyGeoJson layer: Failed to refresh data', err));
    }
});

const internalOverlayLayer = L.GeoJSON.extend({
    statics: {
        _instances: [],
        _batchTimeout: null,
        _requestBatch: function(map) {
            if (this._batchTimeout) {
                clearTimeout(this._batchTimeout);
            }
            this._batchTimeout = setTimeout(() => {
                this._executeBatch(map);
                this._batchTimeout = null;
            }, 50);
        },
        _executeBatch: function(map) {
            const bounds = map.getBounds();
            const requestId = Date.now();

            const activeInstances = this._instances.filter(instance => instance._shouldLoad(map));

            const internalInstances = activeInstances.filter(instance => !instance._type.isReferenceData);
            const referenceInstances = activeInstances.filter(instance => instance._type.isReferenceData);

            if (internalInstances.length > 0) {
                const types = internalInstances.map(instance => {
                    instance._lastRequestId = requestId;
                    return instance._type.name;
                });

                getInternalOverlays(bounds, types).then(data => {
                    internalInstances.forEach(instance => {
                        if (instance._lastRequestId !== requestId) return;
                        const geoJSON = data[instance._type.name] || { type: "FeatureCollection", features: [] };
                        instance.clearLayers();
                        instance.addData(geoJSON);
                    });
                }).catch(err => console.error("Failed to load batch internal overlays", err));
            }

            referenceInstances.forEach(instance => {
                instance._lastRequestId = requestId;
                getReferenceOverlay(bounds, instance._type.name).then(geoJSON => {
                    if (instance._lastRequestId !== requestId) return;
                    instance.clearLayers();
                    instance.addData(geoJSON);
                }).catch(err => console.error(`Failed to load reference overlay ${instance._type.name}`, err));
            });
        }
    },

    initialize: function (type, map, options) {
        this._type = type;
        this._enabled = options.enabled ?? false;

        internalOverlayLayer._instances.push(this);

        options.pointToLayer = (geoJSON, latlng) => {
            const type = geoJSON.properties?.type?.name ?? geoJSON.properties?.type ?? 'default';
            return L.gendlocMarker(type, latlng, { properties: {id: geoJSON.id, ...geoJSON.properties} });
        };

        const userOnEachFeature = options.onEachFeature;
        options.onEachFeature = (feature, layer) => {
            if (userOnEachFeature) {
                userOnEachFeature(feature, layer);
            }

            if (feature.geometry.type !== 'Point') {
                layer.on('click', (e) => {
                    layer.bindPopup(L.overlayPopup(feature.properties, { latlng: e.latlng, featureId: feature.id })).openPopup();
                });
            }
            layer.bindTooltip(feature.properties.name ?? '--');

            if (layer.pm) {
                layer.pm.setOptions({
                    draggable: false,
                    allowEditing: false,
                    allowRemoval: false
                });
            }

            const style = OVERLAY_STYLES[type.name];

            if (typeof style === 'function') {
                layer.setStyle({
                    ...style(feature.properties),
                    ...(feature.properties.style || {})
                });
            }
        };

        map.on('overlayadd', ({ layer }) => {
            if (layer == this) {
                this._enabled = true;
                this._loadData(map);
            }
        });

        map.on('overlayremove', ({ layer }) => {
            if (layer == this) {
                this._enabled = false;
                this.clearLayers();
            }
        });

        map.on('zoomend', () => {
            if (!type.zoom_auto_min || !type.zoom_auto_max) {
                return;
            }

            const zoom = map.getZoom();
            if (zoom >= type.zoom_auto_min && zoom <= type.zoom_auto_max) {
                if (!this._enabled) {
                    map.addLayer(this);
                    map.fire("overlayadd", { layer: this, name: translator.t(`overlay.type.${type.name}`) });
                }
            } else {
                if (this._enabled) {
                    map.removeLayer(this);
                    map.fire("overlayremove", { layer: this, name: translator.t(`overlay.type.${type.name}`) });
                }
            }
        });

        map.on('moveend', () => {
            this._loadData(map);
        });

        L.GeoJSON.prototype.initialize.call(this, {
            type: "FeatureCollection",
            features: []
        }, {
            ...options,
        });

        this._loadData(map);
    },

    _shouldLoad: function (map) {
        if (!map.hasLayer(this)) { return false; }

        const zoom = map.getZoom();
        let zoomCondition = true;

        if (this._type.zoom_min) {
            zoomCondition = zoomCondition && zoom >= this._type.zoom_min;
        }

        if (this._type.zoom_max) {
            zoomCondition = zoomCondition && zoom <= this._type.zoom_max;
        }

        return this._enabled && zoomCondition;
    },

    _loadData: function (map) {
        internalOverlayLayer._requestBatch(map);
    }
});

(() => {
    L.lazyGeoJSON = (remote, options) => new lazyGeoJSON(remote, options);
    L.internalOverlayLayer = (type, map, options = {}) => new internalOverlayLayer(type, map, options);
    L.internalOverlayLayer._instances = internalOverlayLayer._instances;
})();
